/**
 * Purpose: Shared survey/vision axis bounds, trait pairs, and score mapping helpers.
 * Inputs: Slider/vision 0–100 values and raw questionnaire axis scores.
 * Outputs: Skin-type codes, raw scores, and radar-normalized axis values.
 */
export const SKIN_TYPES = [
  'OSPW','OSPT','OSNW','OSNT','ORPW','ORPT','ORNW','ORNT',
  'DSPW','DSPT','DSNW','DSNT','DRPW','DRPT','DRNW','DRNT',
] as const

export type SkinType = typeof SKIN_TYPES[number]

export const AXIS_CONFIG = {
  oilyDry:            { min: 9,  max: 36, threshold: 23 },
  sensitiveResistant: { min: 16, max: 64, threshold: 33 },
  pigmented:          { min: 11, max: 44, threshold: 28 },
  wrinkled:           { min: 18, max: 72, threshold: 45 },
} as const

export type AxisKey = keyof typeof AXIS_CONFIG

export const AXIS_TRAITS = {
  oilyDry:            ['oily', 'dry'] as const,
  sensitiveResistant: ['sensitive', 'resistant'] as const,
  pigmented:          ['pigmented', 'non-pigmented'] as const,
  wrinkled:           ['wrinkled', 'tight'] as const,
} as const

/** Axis order used when assembling the 4-letter Baumann-style code. */
const SKIN_TYPE_AXES: AxisKey[] = [
  'oilyDry',
  'sensitiveResistant',
  'pigmented',
  'wrinkled',
]

/** High-end letter first — same pole order as AXIS_TRAITS / computeSkinType. */
const AXIS_TYPE_LETTERS: Record<AxisKey, readonly [string, string]> = {
  oilyDry: ['O', 'D'],
  sensitiveResistant: ['S', 'R'],
  pigmented: ['P', 'N'],
  wrinkled: ['W', 'T'],
}

/**
 * Piecewise linear so slider 50 always equals the axis threshold:
 *   0   → min
 *   50  → threshold
 *   100 → max
 */
function getAxisBounds(axis: AxisKey): { min: number; max: number; threshold: number } {
  switch (axis) {
    case 'oilyDry':
      return AXIS_CONFIG.oilyDry
    case 'sensitiveResistant':
      return AXIS_CONFIG.sensitiveResistant
    case 'pigmented':
      return AXIS_CONFIG.pigmented
    case 'wrinkled':
      return AXIS_CONFIG.wrinkled
  }
}

export function sliderToScore(value: number, axis: AxisKey): number {
  const { min, max, threshold } = getAxisBounds(axis)
  if (value <= 50) {
    return Math.round(min + (value / 50) * (threshold - min))
  }
  return Math.round(threshold + ((value - 50) / 50) * (max - threshold))
}

/**
 * Derives the 4-letter skin type from Claude Vision 0–100 ratings.
 * Matches production: sliderToScore → compare to AXIS_CONFIG threshold (≥ → high trait).
 */
export function skinTypeFromVisionRatings(
  ratings: Record<AxisKey, number>,
): string {
  // Same threshold rule as computeSkinType so Vision Eval labels match survey codes.
  return SKIN_TYPE_AXES.map((axis) => {
    const score = sliderToScore(ratings[axis], axis)
    const { threshold } = getAxisBounds(axis)
    const [highLetter, lowLetter] = AXIS_TYPE_LETTERS[axis]
    return score >= threshold ? highLetter : lowLetter
  }).join('')
}

/** Floor so min-score axes don't collapse to the center of the results radar chart. */
export const RADAR_MIN = 10

/** Maps a raw axis score to the 10-100 scale shown on the products-page results radar chart. */
export function normalizeAxisScore(score: number, axis: AxisKey): number {
  const { min, max } = getAxisBounds(axis)
  return Math.round(RADAR_MIN + ((score - min) / (max - min)) * (100 - RADAR_MIN))
}
